//todo: token expiry
import { ReplaySubject, Subject, combineLatest } from 'rxjs'
import { take, distinctUntilChanged } from 'rxjs/operators'
import io from 'socket.io-client'
import feathers from '@feathersjs/feathers'
import socketio from '@feathersjs/socketio-client'

export class NotAuthenticated {
    constructor(public name: string, public message: string) {}
}

type payload = {
    isAnon: boolean
    iat: number // issued at
    exp: number // expires at
    aud: string // adience
    iss: string // issuer
    sub: string // string
    jti: string // JWT ID
}

type user = {
    _id: string
    gender: 'f' | 'm'
    isAnon: boolean
    email: string
    isPublic: boolean
    partner: string
    ratedCards: {
        id: string
    }[]
}

export default new (class {
    accessToken: ReplaySubject<string> = new ReplaySubject(1)
    jwtPayload: ReplaySubject<payload> = new ReplaySubject(1)
    authenticated: ReplaySubject<boolean> = new ReplaySubject(1)
    user: ReplaySubject<user> = new ReplaySubject(1)
    connected: ReplaySubject<boolean> = new ReplaySubject(1)
    _app: feathers.Application<any>
    socket: SocketIOClient.Socket
    socketConnected: Subject<boolean> = new Subject()
    socketAuthenticated: ReplaySubject<boolean> = new ReplaySubject(1)
    domain =
        location.hostname === 'localhost' ? 'localhost:3030' : 'api.sexiertogether.apriol.com'
    httpDomain = location.hostname === 'localhost' ? 'http://' + this.domain : 'https://' + this.domain;

    constructor() {
        this._app = feathers()
        this.socket = io(this.domain)
        this._app.configure(socketio(this.socket))
        this.socket.on('connect', () => this.connected.next(true))
        this.socket.on('disconnect', () => this.connected.next(false))
        this.accessToken.subscribe(token => {
            console.log('new token', token)
            localStorage.setItem('accessToken', token)
            try {
                this.jwtPayload.next(this.parseJwt(token))
            } catch (e) {
                console.error('parsing failed')
                // not sure what to do here... :/
                this.notAuthenticated()
            }
        })
        const storedAccessToken = localStorage.getItem('accessToken')
        if (storedAccessToken) {
            this.accessToken.next(storedAccessToken)
        }
        this.jwtPayload.subscribe(this.checkJwtValidity.bind(this))
        this.socket.on('connect', () => {
            this.socketConnected.next(true)
        })
        this.socket.on('disconnect', () => {
            this.socketConnected.next(false)
        })
        combineLatest(
            this.authenticated.pipe(distinctUntilChanged()),
            this.socketConnected
        ).subscribe(([authenticated, connected]) => {
            console.log(authenticated, connected)
            if (authenticated && connected) {
                this.updateUser()
            }
        })
    }

    get app() {
        return (async () => {
            // await this.connected.pipe(take(1)).toPromise()
            await this.socketAuthenticated.pipe(take(1)).toPromise()
            return this._app
        })()
    }
    service(name: string) {
        const f = async (method: string, ...args: any[]) => {
            const app = await this.app
            console.log(name, method, args)
            return app.service(name)[method](...args)
        }
        return {
            get: f.bind(this, 'get'),
            find: f.bind(this, 'find'),
            create: f.bind(this, 'create'),
            update: f.bind(this, 'update'),
            patch: f.bind(this, 'patch'),
            remove: f.bind(this, 'remove'),
        }
    }
    notAuthenticated() {
        if (!location.href.endsWith('/login')) {
            location.href = '/login'
        }
        this.authenticated.next(false)
    }
    async socketRequest(action: string, service: string, data?: any) {
        await this.connected.pipe(take(1)).toPromise()
        return await new Promise((resolve, reject) => {
            this.socket.emit(
                action,
                service,
                data,
                async (error: any, newAuthResult: any) => {
                    if (error) {
                        reject(error)
                    } else {
                        resolve(newAuthResult)
                    }
                }
            )
        })
    }
    async updateUser() {
        const accessToken = await this.accessToken.pipe(take(1)).toPromise()
        const response = (await this.socketRequest('create', 'authentication', {
            strategy: 'jwt',
            accessToken,
        })) as any
        this.socketAuthenticated.next(true)
        console.log('authenticated websocket', response)
        // const user = await this.request('get', 'users/me')
        this.user.next(response.user)
    }
    async checkJwtValidity(payload: payload) {
        if (payload.exp < Date.now() / 1000) {
            this.notAuthenticated()
            return false
        } else {
            this.authenticated.next(true)
            return true
        }
    }
    async login(email: string, password: string, appAuthentication = true) {
        const response = await fetch(
            `${this.httpDomain}/authentication`,
            {
                method: 'post',
                body: JSON.stringify({
                    strategy: 'local',
                    email,
                    password,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
        if (response.status === 401) {
            const data = await response.json()
            throw new NotAuthenticated(data.name, data.message)
        }
        const data = await response.json()
        if (appAuthentication) {
            this.accessToken.next(data.accessToken)
        }
        return data
    }
    parseJwt(token: string) {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function(c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                })
                .join('')
        )

        return JSON.parse(jsonPayload)
    }
    async request(
        httpMethod: string,
        method: string,
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        data?: any,
        authenticate: boolean | { accessToken: string } = true
    ) {
        const headers: Headers = new Headers()
        headers.append('Content-Type', 'application/json')
        if (authenticate) {
            let accessToken, payload
            if (authenticate === true) {
                accessToken = await this.accessToken.pipe(take(1)).toPromise()
                payload = await this.jwtPayload.pipe(take(1)).toPromise()
            } else {
                accessToken = authenticate.accessToken
                payload = this.parseJwt(accessToken)
            }
            const stillValid = await this.checkJwtValidity(payload)
            if (!stillValid) {
                return
            }
            headers.append('Authorization', `Bearer ${accessToken}`)
        }
        const response = await fetch(
            `${this.httpDomain}/${method}`,
            {
                method: httpMethod,
                body: data ? JSON.stringify(data) : undefined,
                headers,
            }
        )
        if (response.status === 401) {
            const data = await response.json()
            throw new NotAuthenticated(data.name, data.message)
        }
        return await response.json()
    }
})()
